import { useCallback, useEffect, useRef } from 'react';
import useLineGraphData, { STOCK_CHART_DEFAULT_PERIOD, STOCK_CHART_DEFAULT_PERIODS, STOCK_CHART_DEFAULT_PERIODS_WITHOUT_1D, } from 'design-system/LineChart/useLineGraphData';
import useFetch from 'hooks/fetch';
import { noop } from 'lodash';
import { useAppStore } from 'store/hooks';
import { mapStockChartPeriodToDateRange } from '../utils';
import { getHistoricalPrices } from '../actions';
import { getChartData } from '../utils/portfolio';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
const fetchOptions = {
    muteFetching: true,
};
const useStockChart = (assetId, has1DChart) => {
    const store = useAppStore();
    const requestingCache = useRef({
        '1D': true,
        '1W': true,
        '1M': true,
        '3M': true,
        '1Y': true,
        YTD: true,
        '5Y': true,
        MAX: true,
    });
    const isStillMounted = useRef(true);
    const [fetch] = useFetch(fetchOptions);
    const defaultPeriod = has1DChart ? STOCK_CHART_DEFAULT_PERIOD : '5Y';
    const periods = has1DChart ? STOCK_CHART_DEFAULT_PERIODS : STOCK_CHART_DEFAULT_PERIODS_WITHOUT_1D;
    const { portfolioDataSet, setPortfolioDataSet, selectedPeriod, setSelectedPeriod, selectedInfoObj } = useLineGraphData(periods, defaultPeriod);
    const refresh = useCallback((dateRange) => {
        if (assetId) {
            const parsedDateRange = dateRange || defaultPeriod;
            requestingCache.current[parsedDateRange] = true;
            const params = {
                assetId,
                dateRange: mapStockChartPeriodToDateRange(parsedDateRange),
            };
            const fetchOtherPeriodsIfNeeded = () => {
                if (dateRange === undefined) {
                    // Fetch the data for other time periods
                    periods.forEach((period) => {
                        if (period !== defaultPeriod) {
                            refresh(period);
                        }
                    });
                }
            };
            fetch(getHistoricalPrices(params))
                .then((response) => {
                if (response && isStillMounted.current) {
                    if (response?.closePrices) {
                        const currentPrice = store.getState().invest.currentPriceCaches[assetId];
                        const isTradingAccountAlpaca = selectIsTradingAccountAlpaca(store.getState());
                        setPortfolioDataSet((oldState) => ({
                            ...oldState,
                            [parsedDateRange]: getChartData(response, parsedDateRange, currentPrice?.currency || (isTradingAccountAlpaca ? 'USD' : 'GBP')),
                        }));
                    }
                }
            })
                .catch(() => { })
                .finally(() => {
                requestingCache.current[parsedDateRange] = false;
                fetchOtherPeriodsIfNeeded();
            });
        }
    }, [assetId, defaultPeriod, periods, setPortfolioDataSet]);
    useEffect(() => {
        if (!portfolioDataSet[selectedPeriod] && !requestingCache.current[selectedPeriod]) {
            refresh(selectedPeriod);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, selectedPeriod]);
    useEffect(() => () => {
        isStillMounted.current = false;
    }, []);
    useEffect(() => {
        if (assetId) {
            const timeout = setTimeout(() => {
                refresh();
            }, 300);
            return () => {
                clearTimeout(timeout);
            };
        }
        return noop;
    }, [assetId, refresh]);
    return {
        requesting: !portfolioDataSet[selectedPeriod],
        dataSet: portfolioDataSet,
        selectedPeriod: selectedPeriod,
        setSelectedPeriod: setSelectedPeriod,
        selectedInfoObj: selectedInfoObj,
    };
};
export default useStockChart;
